// A meeting — in the user's words a "пространство", a place that lives on, as a server of Discord does:
// what the server says of it — the meeting the user is let into, its channels and moderators, the chat of
// the text channel shown, who is in it and in its voice channels, their transcripts, the host's acts — and
// what the user does: makes one, opens one by its link, goes from channel to channel, writes, leaves; the
// host makes, renames and deletes channels, makes moderators, mutes, removes and ends; the host and the
// moderators begin, go on with and stop transcripts.
//
// The page opens — hears — only the channel it shows and the call's: going to another channel opens it
// and leaves the one before, unless the call is there. The chat of a text channel is chat.ts's.
import { welcomed } from "./account.ts";
import { onBoardDone, onBoardRefused, onBoardState, onBoards } from "./boards.ts";
import { answered, hangup, joinCall, microphone, roomed, shared, slotted, unshared } from "./call.ts";
import { NEWEST, PAGE, onChat, rearm, resetChat } from "./chat.ts";
import { send } from "./net.ts";
import type { Server, ServerMsg } from "./protocol.ts";
import { prove } from "./work.ts";
import {
  MEETING_TITLE,
  changed,
  channelOf,
  inMeeting,
  isHost,
  isModerator,
  nameOf,
  meetingTitle,
  ofMeeting,
  show,
  state,
  transcriptsOf,
  type Line,
  type Pane,
  type View,
} from "./store.ts";

export const ID = /^[a-z2-7]{16}$/;
export const WEB_LINK = /\/m\/([a-z2-7]{16})\/?$/;
/** Lines of a transcript a page asks for, and of one downloaded. */
const LINES = 200;
const WHOLE = 500;

export function onServer(m: ServerMsg): void {
  switch (m.type) {
    case "welcome":
      welcomed(m.name);
      state.avatar = m.avatar;
      // A new session: it has none of the uploads of the one before.
      rearm();
      // The start screen shows the account's meetings.
      if (state.meeting === null) send({ type: "mine" });
      return changed();
    case "mine":
      state.mine = m.meetings.map((x) => ({ ...x, title: meetingTitle(x.title) }));
      return changed();
    case "meeting":
      return entered(m);
    case "layout":
      return laid(m);
    case "page":
      onChat(m);
      return askNames([
        ...new Set(
          m.messages.flatMap((x) => [x.from, ...(x.reply === null || x.reply.from === 0 ? [] : [x.reply.from])]),
        ),
      ]);
    case "message":
      onChat(m);
      return askNames([m.from]);
    case "edited":
    case "deleted":
    case "mentioned":
    case "uploaded":
      return onChat(m);
    case "reacted":
      onChat(m);
      return askNames([m.user]);
    case "presence":
      if (!inMeeting(m.community)) return;
      for (const id of m.online) {
        state.online.add(id);
        // One asked about while away, whose name the server did not know then: asked about again.
        if (!state.names.has(id)) state.asked.delete(id);
      }
      for (const id of m.offline) state.online.delete(id);
      askNames(m.online);
      return changed();
    case "members":
      if (inMeeting(m.community)) askNames(m.users);
      return;
    case "names":
      if (!inMeeting(m.community)) return;
      m.users.forEach((id, i) => {
        if (m.names[i] !== "") state.names.set(id, m.names[i]);
        if (m.avatars[i] !== "") state.avatars.set(id, m.avatars[i]);
        else state.avatars.delete(id);
        state.asked.add(id);
      });
      return changed();
    case "typing":
      onChat(m);
      return askNames(state.typists);
    case "error":
      return refused(m.text);
    case "hosted":
      return hosted(m);
    case "rtc":
      return void answered(m);
    case "share":
      return void shared(m);
    case "slots":
      return slotted(m);
    case "room":
      return roomed(m);
    case "voiceUsers":
      if (!ofMeeting(m.channel)) return;
      state.voices.set(m.channel, { users: m.users, cameras: m.cameras, screens: m.screens });
      askNames(m.users);
      return changed();
    case "transcribing":
      return transcribing(m);
    case "line":
      return lined(m);
    case "transcripts":
      return listed(m);
    case "transcript":
      return transcribed(m);
    case "work":
      return void worked(m);
    case "boards":
      return onBoards(m);
    case "boardState":
      return onBoardState(m);
    case "board":
      return onBoardDone(m);
    case "boardRefused":
      onBoardRefused(m);
      // Another's act got there first — the shape erased or closed meanwhile: nothing to tell.
      if (!RACED.includes(m.text)) show(SAID[m.text] ?? m.text, true);
      return;
    default:
    // A client of datagrams' voice (`voice`) and how its packets come (`quality`); the challenge (net.ts).
  }
}

/** Whether the middle shows channel `channel` of kind `kind`. */
function shows(kind: View["kind"], channel: number): boolean {
  return state.meeting !== null && state.view.kind === kind && state.view.channel === channel;
}

/** The server let the user into a meeting: its first text channel shows — after a reconnection, the
 * channel shown before, from the newest message the page has, and the call again. */
function entered(m: Server<"meeting">): void {
  const again = inMeeting(m.number);
  // The user's number and name in this meeting: each meeting gives others.
  state.user = m.you;
  state.shown = m.name;
  state.meeting = { id: m.id, number: m.number, host: m.host, title: meetingTitle(m.title) };
  state.wanted = null;
  state.creating = false;
  if (location.pathname !== `/m/${m.id}`) history.pushState(null, "", `/m/${m.id}${location.search}`);
  if (!again) {
    state.layout = { channels: [], moderators: [] };
    state.view = { kind: "text", channel: m.number };
    state.voices = new Map();
    state.transcripts = new Map();
    state.online = new Set([state.user]);
    state.pane = "chat";
    resetChat();
    show("");
  }
  state.names.set(state.user, m.name);
  // The server forgot what the page had open with the connection.
  state.opened = new Set();
  open(state.view.channel, state.view.kind === "text" ? state.last : 0);
  if (again && state.wantCall >= 0) open(state.wantCall, 0);
  send({ type: "members", community: m.number, from: 0, limit: 200 });
  if (state.view.kind === "voice") askTranscripts(state.view.channel);
  // A transcript shown comes again: lines were said while the connection was down.
  if (again) {
    for (const [ch, t] of state.transcripts) if (state.opened.has(ch) && t.shown !== 0) showTranscript(ch, t.shown);
  }
  if (again && state.wantCall >= 0 && state.call === null) void joinCall(state.wantCall);
  changed();
}

/** Channel `channel` opened — its messages, or lines, come from now on — unless it is already; a text
 * channel's page from after message `after`. */
function open(channel: number, after: number): void {
  if (state.meeting === null || state.opened.has(channel)) return;
  state.opened.add(channel);
  send({ type: "join", community: state.meeting.number, channel, after });
}

/** The channels opened but neither shown nor the call's are left. */
function release(): void {
  for (const ch of [...state.opened]) {
    if (ch === state.view.channel || ch === state.call?.channel) continue;
    state.opened.delete(ch);
    send({ type: "leave", channel: ch });
  }
}

/** The middle shows channel `channel` of kind `kind`: opened, the one before left. A text channel shows
 * its newest messages — asked for, if the channel was open already (a voice channel of its number is: the
 * first two of a meeting have one); a voice channel the list of its transcripts, and the one shown or the
 * one written as it is now, unless the channel was heard all along (the call's). */
export function view(kind: View["kind"], channel: number): void {
  // A phone shows the middle: in the call, its tiles.
  state.pane = kind === "voice" && state.call?.channel === channel ? "voice" : "chat";
  if (kind === "text") state.mentions.delete(channel);
  if (shows(kind, channel)) return changed();
  const heard = state.opened.has(channel);
  if (kind === "text") {
    resetChat();
    if (heard) send({ type: "history", channel, before: NEWEST, limit: PAGE });
  }
  state.view = { kind, channel };
  open(channel, 0);
  release();
  if (kind === "voice") {
    askTranscripts(channel);
    const t = transcriptsOf(channel);
    const shown = t.shown !== 0 ? t.shown : t.writing;
    if (shown !== 0 && (!heard || t.shown === 0)) showTranscript(channel, shown);
  }
  changed();
}

/** The user joins voice channel `channel`: it opens — its transcript's lines come while the user is in
 * it, whatever shows — the call's channel before is left unless shown, and a phone shows the tiles. */
export function joinVoice(channel: number): void {
  open(channel, 0);
  if (shows("voice", channel)) state.pane = "voice";
  // The call before ends at once; the new one is the user's from now on.
  void joinCall(channel);
  release();
  changed();
}

/** The user leaves the call: its channel is left unless shown; a phone shows the channel's page. */
export function leaveCall(): void {
  hangup(true);
  release();
  state.pane = "chat";
  changed();
}

/** The meeting's channels and moderators, as the host made them: a channel shown or with the call that
 * is gone, goes — the meeting's first text channel shows. */
function laid(m: Server<"layout">): void {
  if (!inMeeting(m.meeting)) return;
  state.layout = { channels: m.channels, moderators: m.moderators };
  const call = state.call;
  if (call !== null && channelOf(call.channel, "voice") === null) {
    hangup(true);
    show("голосовой канал удалён", true);
  }
  if (channelOf(state.view.channel, state.view.kind) === null) {
    show(state.view.kind === "text" ? "канал удалён" : "голосовой канал удалён", true);
    view("text", m.meeting);
  }
  release();
  changed();
}

/** The names and pictures of `users` the client does not know yet, asked for. */
export function askNames(users: number[]): void {
  if (state.meeting === null) return;
  const unknown = users.filter((id) => !state.names.has(id) && !state.asked.has(id));
  if (unknown.length === 0) return;
  unknown.forEach((id) => state.asked.add(id));
  send({ type: "names", community: state.meeting.number, users: unknown });
}

/** Why a meeting cannot be opened, in the user's words: the meeting is left. */
const SHUT: Record<string, string> = {
  "no such meeting": "такого пространства нет",
  "the meeting has ended": "пространство закрыто",
  "the host removed you from this meeting": "владелец удалил вас из пространства",
  "the meeting is closed to you": "пространство для вас закрыто",
  "a meeting opens by its link": "пространство открывается только по ссылке",
};

/** What else the server does not do, in the user's words: its limits among them. */
const SAID: Record<string, string> = {
  "only the host can do that": "это может только владелец",
  "only the host and moderators can do that": "это могут только владелец и модераторы",
  "too many requests: slow down": "слишком много запросов — подождите немного",
  "too many messages from your address: slow down": "слишком много сообщений с вашего адреса — подождите немного",
  "the message is too long": "сообщение слишком длинное",
  "too many new meetings: try again later": "слишком много новых пространств — попробуйте позже",
  "too many new meetings on the server: try again in a few minutes":
    "сейчас создаётся слишком много пространств — попробуйте через несколько минут",
  "too many new channels from your address: try again later":
    "слишком много новых каналов с вашего адреса — попробуйте позже",
  "too many connections from your address": "слишком много подключений с вашего адреса",
  "too many requests refused: the connection closes": "слишком много отклонённых запросов — соединение закрыто",
  "a screen is shown from the voice channel": "экран показывают из голосового канала",
  "transcripts are off on this server": "расшифровка на этом сервере не включена",
  "no such transcript": "такой расшифровки нет",
  "no such channel": "такого канала нет",
  "the channel was deleted": "канал удалён",
  "a voice channel has no chat": "в голосовом канале нет чата",
  "the first channels stay": "первые каналы пространства не удаляются",
  "no more channels here": "больше каналов в этом пространстве не сделать",
  "a message has text or images": "в сообщении нет ни текста, ни изображений",
  "too many images in a message": "в сообщении не больше 10 изображений",
  "no such image: upload it first": "изображение не загрузилось — отправьте его ещё раз",
  "no such message": "такого сообщения нет",
  "only its writer edits a message": "править сообщение может только его автор",
  "only its writer, the host and moderators delete a message":
    "удалить сообщение могут только его автор, владелец и модераторы",
  "a reaction is an emoji": "реакция — это эмодзи",
  "too many reactions to the message": "у сообщения слишком много разных реакций",
  "too many pictures: try again later": "аватар меняли слишком часто — попробуйте позже",
  "the picture is too large": "картинка аватара слишком большая",
  "you are not in the call": "вы не в звонке",
  "the board is closed": "доска закрыта",
  "the call has as many boards as it may": "в звонке уже три доски — больше не открыть",
  "only its author, the host and moderators close a board": "закрыть доску могут её автор, владелец и модераторы",
  "only its author, the host and moderators clear a board": "очистить доску могут её автор, владелец и модераторы",
  "only the host and moderators say who draws": "кто рисует, решают владелец и модераторы",
  "only the host and moderators draw on this board": "на этой доске рисуют только владелец и модераторы",
  "too many shapes at once": "слишком много фигур сразу",
  "the board is full": "на доске больше нет места",
  "a shape of that id is on the board": "такая фигура уже на доске",
  "a shape's id is too long": "у фигуры слишком длинный номер",
  "a shape's points are wrong": "у фигуры неверные точки",
  "a shape's color is wrong": "у фигуры неверный цвет",
  "a shape's line is too wide": "у фигуры слишком толстая линия",
  "a text is too long, or its size is wrong": "надпись слишком длинная или неверного размера",
  "only its drawer draws on a pen's path": "продолжить линию может только тот, кто её рисует",
  "a pen's path is too long": "линия слишком длинная",
  "the shapes would leave the board": "фигуры ушли бы с доски",
};

/** Refusals of a board's acts that only say another's act came first: the page says nothing of them. */
const RACED = ["the shape is gone", "the shapes are gone", "the board is closed"];

/** What the server did not do, in the user's words; a meeting that cannot be opened is left. */
function refused(text: string): void {
  const shut = SHUT[text];
  if (shut !== undefined && (state.meeting !== null || state.wanted !== null)) {
    state.wanted = null;
    return leave(shut);
  }
  // A meeting not made: the user may try again.
  if (text.startsWith("too many new meetings")) state.creating = false;
  // A message refused does not come back: none is waited for. One the server took comes back as it
  // would, and shows.
  if (text.startsWith("too many messages") || text.startsWith("too many requests") || text === "the message is too long")
    for (const [nonce, p] of state.pending) if (!p.waiting) state.pending.delete(nonce);
  // A screen not taken is not shown.
  if (text === "a screen is shown from the voice channel") unshared();
  show(shut ?? SAID[text] ?? text, true);
}

/** The host did something to the user. */
function hosted(m: Server<"hosted">): void {
  if (!inMeeting(m.meeting)) return;
  if (m.act === "mute") {
    microphone(false);
    return show("владелец выключил вам микрофон");
  }
  leave(m.act === "end" ? "пространство закрыто" : "владелец удалил вас из пространства");
}

/** The user leaves the meeting: the start screen shows, with why. */
export function leave(why = ""): void {
  state.wantCall = -1;
  hangup(true);
  for (const ch of state.opened) send({ type: "leave", channel: ch });
  state.opened = new Set();
  state.meeting = null;
  state.room = { sessions: [], users: [], cameras: [], screens: [] };
  state.voices = new Map();
  state.transcripts = new Map();
  state.layout = { channels: [], moderators: [] };
  state.pane = "chat";
  state.drafts = new Map();
  state.pending = new Map();
  state.mentions = new Map();
  state.lightbox = null;
  state.dialog = null;
  if (location.pathname !== "/") history.pushState(null, "", `/${location.search}`);
  state.shown = "";
  show(why, why !== "");
  send({ type: "mine" });
}

/** A new meeting, the user its host. */
export function newMeeting(): void {
  state.creating = true;
  state.wanted = null;
  show("создаю пространство…");
  send({ type: "create", title: MEETING_TITLE, proof: -1 });
}

/** The challenge of the work the server asked last: a number found for an older one is not sent. */
let asked: Uint8Array | null = null;

/** The server asks work for the new meeting (ADR-0003): the page does it — a moment, longer when meetings
 * are made too fast — and asks again with the number found. */
async function worked(m: Server<"work">): Promise<void> {
  if (!state.creating) return;
  asked = m.challenge;
  show(m.bits > 20 ? "создаю пространство — сейчас это дольше обычного…" : "создаю пространство…");
  const proof = await prove(m.challenge, m.bits);
  if (state.creating && asked === m.challenge) send({ type: "create", title: MEETING_TITLE, proof });
}

/** The meeting of a link or id typed: on this server, or another's page. */
export function openLink(text: string): void {
  const t = text.trim();
  const m = t.match(WEB_LINK);
  if (m !== null && /^https?:\/\//.test(t) && !t.startsWith(location.origin)) {
    // Another server's meeting: its own page.
    location.href = t;
    return;
  }
  const id = m !== null ? m[1] : ID.test(t) ? t : null;
  if (id === null) return show("это не ссылка на пространство", true);
  meet(id);
}

export function meet(id: string): void {
  state.wanted = id;
  state.joining = false;
  show("вхожу в пространство…");
  send({ type: "meet", id });
}

/** The meeting's link: the page of the web client. */
export function link(): string {
  return state.meeting === null ? "" : `${location.origin}/m/${state.meeting.id}`;
}

/** Inviting to the meeting: a phone shares its link as it shares anything; elsewhere a dialog shows it —
 * to copy, and its QR code for a phone's camera. */
export function invite(): void {
  if (typeof navigator.share === "function" && matchMedia("(pointer: coarse)").matches) return void share();
  state.dialog = { kind: "invite" };
  changed();
}

/** The account's meetings, asked for again: the meeting's menu lists them. */
export function askMine(): void {
  send({ type: "mine" });
}

/** Meeting `id` leaves the account's list — an ended one, which nobody enters any more: here at once, and the
 * server forgets it and sends the list again. */
export function forget(id: string): void {
  state.mine = state.mine.filter((m) => m.id !== id);
  changed();
  send({ type: "forget", id });
}

/** The user goes from this meeting to meeting `id`. */
export function switchTo(id: string): void {
  leave();
  meet(id);
}

/** The user leaves this meeting for a new one. */
export function startNew(): void {
  leave();
  newMeeting();
}

/** The link shared (a phone) or copied. */
export async function share(): Promise<void> {
  const url = link();
  if (navigator.share && matchMedia("(pointer: coarse)").matches) {
    try {
      await navigator.share({ title: state.meeting?.title, url });
      return;
    } catch {
      // Not shared: copied instead.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    show("ссылка скопирована");
  } catch {
    prompt("Ссылка на пространство:", url);
  }
}

/** What the host does to `user` (or the meeting), asked first where it cannot be undone. */
export function host(act: "mute" | "remove" | "end", user: number): void {
  if (state.meeting === null) return;
  const asked =
    act === "remove"
      ? `Удалить ${nameOf(user)} из пространства? Вернуться по ссылке не получится.`
      : act === "end"
        ? "Закрыть пространство для всех? Войти в него больше не получится."
        : null;
  if (asked !== null && !confirm(asked)) return;
  send({ type: "host", meeting: state.meeting.number, act, user });
}

/** The host makes a channel of kind `kind`, named as the user says. */
export function addChannel(kind: "text" | "voice"): void {
  if (state.meeting === null) return;
  const name = prompt(kind === "text" ? "Название текстового канала:" : "Название голосового канала:", "");
  if (name === null || name.trim() === "") return;
  send({ type: "addChannel", meeting: state.meeting.number, kind, name: name.trim() });
}

/** The host renames channel `channel` of kind `kind`, as the user says. */
export function renameChannel(channel: number, kind: "text" | "voice", was: string): void {
  if (state.meeting === null) return;
  const name = prompt("Новое название канала:", was);
  if (name === null || name.trim() === "" || name.trim() === was) return;
  send({ type: "editChannel", meeting: state.meeting.number, channel, kind, name: name.trim(), deleted: false });
}

/** The host deletes channel `channel` of kind `kind`, asked first. */
export function deleteChannel(channel: number, kind: "text" | "voice", name: string): void {
  if (state.meeting === null) return;
  if (!confirm(`Удалить канал «${name}»? Он пропадёт у всех.`)) return;
  send({ type: "editChannel", meeting: state.meeting.number, channel, kind, name: "", deleted: true });
}

/** The host makes `user` a moderator — who controls transcripts — or not. */
export function moderate(user: number, on: boolean): void {
  if (state.meeting === null) return;
  send({ type: "moderate", meeting: state.meeting.number, user, on });
}

/** Whether the user controls transcripts: the host, or a moderator. */
export function controls(): boolean {
  return isHost() || isModerator(state.user);
}

/** The transcripts of voice channel `channel` asked for: their list. */
function askTranscripts(channel: number): void {
  send({ type: "transcripts", channel });
}

/** Which transcript of a voice channel is written now: one that begins or goes on shows where the list
 * did or the one written before; the user in the channel's call hears of it. */
function transcribing(m: Server<"transcribing">): void {
  if (!ofMeeting(m.channel)) return;
  const t = transcriptsOf(m.channel);
  const was = t.writing;
  t.writing = m.session;
  if (state.opened.has(m.channel) && m.session !== was) {
    if (m.session !== 0 && (t.shown === 0 || t.shown === was)) showTranscript(m.channel, m.session);
    // The list again: a transcript new, or its counts.
    askTranscripts(m.channel);
  }
  if (state.call?.channel === m.channel && was !== m.session)
    show(m.session !== 0 ? "в голосовом канале идёт расшифровка" : "расшифровка остановлена");
  changed();
}

/** The transcripts of a voice channel, as the server lists them. */
function listed(m: Server<"transcripts">): void {
  if (!ofMeeting(m.channel)) return;
  transcriptsOf(m.channel).sessions = m.sessions;
  changed();
}

/** Those who wait for the whole of the transcript shown of a voice channel, by channel. */
const wholes = new Map<number, () => void>();

/** Transcript `session` of voice channel `channel` shows (0: their list, asked for again): its newest
 * lines are asked for. */
export function showTranscript(channel: number, session: number): void {
  const t = transcriptsOf(channel);
  t.shown = session;
  t.lines = new Map();
  t.from = -1;
  t.all = false;
  t.asking = session !== 0;
  t.saving = false;
  wholes.delete(channel);
  if (session !== 0) send({ type: "transcript", channel, session, before: 0, limit: LINES });
  else askTranscripts(channel);
  changed();
}

/** A line of a transcript: under way, or its last — which stays, and goes if it has no words. Only the
 * transcript shown keeps its lines. */
function lined(l: Line): void {
  if (!ofMeeting(l.channel)) return;
  const t = transcriptsOf(l.channel);
  if (t.shown !== l.session) return;
  const had = t.lines.get(l.seq);
  // One under way that comes after the last is old (they come from two of the server's processes).
  if (had !== undefined && had.final && !l.final) return;
  if (l.final && l.text === "") t.lines.delete(l.seq);
  else t.lines.set(l.seq, l);
  askNames([l.user]);
  changed();
}

/** Lines of a transcript the user asked for: fewer than asked, and there are none older. */
function transcribed(m: Server<"transcript">): void {
  if (!ofMeeting(m.channel)) return;
  const t = transcriptsOf(m.channel);
  if (t.shown !== m.session) return;
  for (const l of m.lines) if (!t.lines.has(l.seq) || !t.lines.get(l.seq)!.final) t.lines.set(l.seq, l);
  t.from = m.from;
  t.all = m.from === 0;
  t.asking = false;
  askNames([...new Set(m.lines.map((l) => l.user))]);
  // Who waits for the whole of it: older lines again, or all of them here.
  const done = wholes.get(m.channel);
  if (done !== undefined && t.all) {
    wholes.delete(m.channel);
    t.saving = false;
    done();
  } else if (done !== undefined) olderLines(m.channel, WHOLE);
  changed();
}

/** The view came near the oldest line of the transcript shown: older ones are asked for — `limit` of
 * them. */
export function olderLines(channel: number, limit = LINES): void {
  const t = transcriptsOf(channel);
  if (t.asking || t.all || t.from <= 0 || t.shown === 0) return;
  t.asking = true;
  send({ type: "transcript", channel, session: t.shown, before: t.from, limit });
}

/** The whole of the transcript shown of voice channel `channel`, its older lines asked for till there
 * are none: done when they are all here — or never, if another shows first. */
export function wholeTranscript(channel: number): Promise<void> {
  const t = transcriptsOf(channel);
  if (t.all) return Promise.resolve();
  return new Promise((done) => {
    wholes.set(channel, done);
    t.saving = true;
    olderLines(channel, WHOLE);
    changed();
  });
}

/** The host or a moderator begins a transcript of voice channel `channel` (`new`), goes on with
 * transcript `session` (`continue`), or stops the one written (`stop`). */
export function transcribe(channel: number, act: "new" | "continue" | "stop", session = 0): void {
  send({ type: "transcribe", channel, act, session });
}

/** What a phone shows. */
export function pane(p: Pane): void {
  state.pane = p;
  changed();
}

